import { useFormContext } from "react-hook-form";
import { ScreenContext } from "./context";
import { useTranslation } from "../../../../utilities/i18n";
import {
  Button,
  Page,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import React from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { ManageTowerPaymentsRootScreenParamList } from "../../../../types";
import {
  TowerPaymentsPairForm,
  TowerPaymentsPairFormProvider,
  TowerPaymentsPairFormValues,
} from "../../components/TowerPaymentsPairForm";
import { Colors } from "react-native-ui-lib";
import { useMutation } from "@apollo/client";
import {
  PaymentMethod,
  TowerPaymentsPairDeviceResult,
} from "@towersystems/roam-common/lib/generated-types";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { MUTATION_PAIR_TOWER_PAYMENTS_DEVICE } from "../../../../common/queries";

export interface PairTowerPaymentsDeviceScreenProps
  extends StackScreenProps<
    ManageTowerPaymentsRootScreenParamList,
    "PairTowerPaymentsDeviceScreen"
  > {}

const PairTowerPaymentsDevice = ({
  paymentMethod,
  navigation,
}: {
  paymentMethod: PaymentMethod;
  navigation: PairTowerPaymentsDeviceScreenProps["navigation"];
}) => {
  const flashMessagesContext = useFlashMessages();

  const [m] = useMutation<{
    pairTowerPaymentsDevice: TowerPaymentsPairDeviceResult;
  }>(MUTATION_PAIR_TOWER_PAYMENTS_DEVICE);

  const { formState, handleSubmit, reset } =
    useFormContext<TowerPaymentsPairFormValues>();

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const { t } = useTranslation("settings");

  React.useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful, reset]);

  const handleOnSubmit = async (data: TowerPaymentsPairFormValues) => {
    try {
      const result = await m({
        variables: {
          pairCode: data.pairCode,
          paymentMethodId: data.paymentMethodId,
        },
      });
      if (
        !result.data ||
        "errorCode" in result?.data?.pairTowerPaymentsDevice
      ) {
        throw new Error(t("messages.pair_tower_payments_device_error.message"));
      }

      if (result.data?.pairTowerPaymentsDevice?.deviceDescription) {
        flashMessagesContext.showMessage({
          severity: "success",
          message: t("messages.pair_tower_payments_device_success.message", {
            deviceDescription:
              result.data.pairTowerPaymentsDevice.deviceDescription,
          }),
        });
        // Navigate back to the previous screen or reset the form
        navigation.goBack();
      } else {
        throw new Error(t("messages.pair_tower_payments_device_error.message"));
      }
    } catch (error) {
      console.log(error);
      flashMessagesContext.showMessage({
        severity: "error",
        message: t("messages.pair_tower_payments_device_error.message"),
      });
    }
  };

  const title = `Pair Tower Payments Device - ${paymentMethod.name}`;

  const secondaryTitle =
    "Pair a new Tower Payments Device with this payment method.";

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.submit_tower_payments_pairing")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page title={title} secondaryTitle={secondaryTitle} footer={footerMarkup}>
      <TowerPaymentsPairForm />
    </Page>
  );
};

export const PairTowerPaymentsDeviceScreen = (
  props: PairTowerPaymentsDeviceScreenProps
) => {
  const context = React.useContext(ScreenContext);
  const transitionEnded = useHasTransitionEnded(props.navigation);
  if (!context?.paymentMethod || !transitionEnded) {
    return <ResourcePageSkeleton />; // or a loading indicator
  }

  return (
    <TowerPaymentsPairFormProvider
      defaultValues={{
        paymentMethodId: context?.paymentMethod.id || "",
      }}
    >
      <PairTowerPaymentsDevice
        paymentMethod={context.paymentMethod}
        navigation={props.navigation}
      />
    </TowerPaymentsPairFormProvider>
  );
};
